/**
 * PATH: packages/email-hub/src/feature/lib/cardAiState.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-CARD-AI-STATE-001
 * PURPOSE: Pure I->O resolver mapping an email's Kanban allocation /
 *          category signals to (a) the inbox card's 3-colour AI status
 *          and (b) its board visibility (private to you vs on the
 *          shared board). ONE source of truth for the card, the reader
 *          header, and tests.
 * WHY:     Operator 2026-06-16 — the 3-colour "AI accepted" signal +
 *          the private/on-board state lived only (2-state) in the
 *          reader header; the inbox cards had neither. The card must
 *          signal at a glance whether AI is a pending suggestion (grey,
 *          private to you), was auto-filed because auto-AI is on
 *          (violet, on board), or was accepted by a human (green, on
 *          board).
 * FLOW:
 *   ┌──────────────────────────┐   ┌────────────────────┐   ┌─────────────────────┐
 *   │ allocation_summary.source│ → │ resolveCardAiState │ → │ { ai, visibility }  │
 *   │ category / category_lock │   │                    │   │                     │
 *   │ suggested_job            │   │                    │   │                     │
 *   └──────────────────────────┘   └────────────────────┘   └─────────────────────┘
 * RELATED:
 *   - components/CardAiTag.tsx: renders `ai`.
 *   - components/CardVisibilityBadge.tsx: renders `visibility`.
 *   - components/AiTriageBanner.tsx: resolveAiBadgeState (the reader's
 *     legacy 2-state badge) — kept independent; this is its 3-state
 *     superset that both surfaces now share.
 */
import type { EmailAllocationSummary } from '@quotech/tenant-shared/email/api'

/** AI acceptance phase the card's "AI" tag colours itself by.
 *  - 'none'     -> no AI signal yet (uncategorised, no allocation): hide.
 *  - 'pending'  -> AI suggested, awaiting the operator's tick (GREY).
 *  - 'auto'     -> AI auto-filed (auto-AI on) or rule-filed (VIOLET).
 *  - 'accepted' -> human accepted / pinned (GREEN). */
export type CardAiState = 'none' | 'pending' | 'auto' | 'accepted'

/** Board visibility of the email's Kanban placement.
 *  - 'none'    -> not on a board (no allocation): hide the badge.
 *  - 'private' -> AI suggestion pending; only THIS user sees it until
 *                 accepted (mirrors ai_auto_apply_allocations=false:
 *                 "they stay private ... until this user accepts").
 *  - 'board'   -> filed on the shared board; visible to board members. */
export type CardVisibility = 'none' | 'private' | 'board'

export interface CardAiResolution {
  ai: CardAiState
  visibility: CardVisibility
}

/** Minimal structural subset of the email-hub `Email` the resolver
 *  needs, so callers can pass the row object directly. */
export interface CardAiInput {
  allocation_summary?: EmailAllocationSummary | null
  category?: string | null
  category_locked?: boolean | null
  suggested_job?: { job_id: number } | null
}

/** Category keys that mean "not classified yet" — these get no AI tag.
 *  Mirrors the reader's `category !== 'uncategorized'` gate (general is
 *  a real, if low-signal, classification and DOES show a tag). */
const UNCLASSIFIED = new Set(['', 'uncategorized', 'uncategorised'])

/**
 * resolveCardAiState(email) -> { ai, visibility }
 *
 * Priority: a live board allocation wins (it carries the authoritative
 * `source`); else a pending AI job suggestion; else the category
 * signal. Visibility is only private/board when there is a real board
 * allocation — a bare category/suggestion has no board placement yet.
 */
export function resolveCardAiState(email: CardAiInput): CardAiResolution {
  const summary = email.allocation_summary
  if (summary) {
    switch (summary.source) {
      case 'USER_MANUAL':
      case 'AI_SUGGESTED_USER_CONFIRMED':
        return { ai: 'accepted', visibility: 'board' }
      case 'AI_AUTO':
      case 'RULE':
        return { ai: 'auto', visibility: 'board' }
      case 'AI_SUGGESTED_PENDING':
      default:
        // A pending suggestion (and any unknown future source) reads as
        // "still needs your tick" — conservative: don't claim it's on
        // the shared board until it is.
        return { ai: 'pending', visibility: 'private' }
    }
  }

  // No board allocation — fall back to category / suggestion signals.
  if (email.suggested_job) {
    return { ai: 'pending', visibility: 'none' }
  }

  const cat = (email.category || '').toLowerCase()
  const hasCategory = !!cat && !UNCLASSIFIED.has(cat)
  if (hasCategory) {
    return email.category_locked
      ? { ai: 'accepted', visibility: 'none' }
      : { ai: 'pending', visibility: 'none' }
  }

  return { ai: 'none', visibility: 'none' }
}
